import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { reconciliationAPI } from '../../../../api';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import { Card, CardContent } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { FormLabel } from '../../../../components/ui/FormLabel';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../../components/ui/Table';
import { ArrowLeft, ExternalLink, AlertTriangle } from 'lucide-react';

/**
 * Reconciliation status report (plan §11).
 *
 * URL: /tenant/banking/reconciliation/status-report
 *
 * Per account for a chosen date range:
 *   Imported   — statement lines within the range (count + sum)
 *   Matched    — currently in a draft match
 *   Reconciled — fully reconciled
 *   Unmatched  — queue (unmatched/suggested/partially/needs_review)
 *   Ignored    — explicitly set aside
 *   Rate       — reconciled / imported by amount (more meaningful than by count)
 *
 * Row click drills through to the per-account workbench with the date range
 * prefilled so the user can act on exceptions immediately.
 */
const currency = (n) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(Number(n ?? 0));

const toInputDate = (d) => new Date(d).toISOString().split('T')[0];

/**
 * UK tax year presets are useful here because reconciliation is often
 * done period-by-period (matching the P&L and VAT cycles).
 */
function presetRanges() {
  const now = new Date();
  const ytdStart = new Date(now.getFullYear(), 0, 1);
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

  // UK tax year runs 6 April to 5 April.
  const taxYearStart =
    now.getMonth() > 3 || (now.getMonth() === 3 && now.getDate() >= 6)
      ? new Date(now.getFullYear(), 3, 6)
      : new Date(now.getFullYear() - 1, 3, 6);

  return [
    { key: 'this-month', label: 'This month', start: thisMonthStart, end: now },
    { key: 'last-month', label: 'Last month', start: lastMonthStart, end: lastMonthEnd },
    { key: 'ytd', label: 'Year to date', start: ytdStart, end: now },
    { key: 'tax-year', label: 'UK tax year', start: taxYearStart, end: now },
  ];
}

export default function ReconciliationStatusReport() {
  const navigate = useNavigate();

  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);

  const initialRange = useMemo(
    () => ({ start_date: toInputDate(defaultStart), end_date: toInputDate(today) }),
    [defaultStart, today]
  );

  const [period, setPeriod] = useState(initialRange);
  const [activePeriod, setActivePeriod] = useState(initialRange);

  const presets = useMemo(() => presetRanges(), []);

  const {
    data,
    isLoading: loading,
    error: queryError,
  } = useQuery(
    ['reconciliation', 'status-report', activePeriod.start_date, activePeriod.end_date],
    () => reconciliationAPI.getStatusReport(activePeriod),
    {
      enabled: Boolean(activePeriod.start_date && activePeriod.end_date),
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    }
  );

  const error = queryError ? queryError?.response?.data?.error || queryError.message : '';

  const accounts = data?.accounts || [];
  const totals = data?.totals || null;

  const applyPreset = (preset) => {
    const range = {
      start_date: toInputDate(preset.start),
      end_date: toInputDate(preset.end),
    };
    setPeriod(range);
    setActivePeriod(range);
  };

  const drillThrough = (accountId) => {
    // The workbench reads its own filters, but linking here at least jumps
    // the user to the right surface. (Wiring URL-param-based filter hydration
    // is a small follow-up; for now we just navigate to the workbench.)
    navigate(`/tenant/banking/reconciliation/accounts/${accountId}`);
  };

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/banking/reconciliation')}
                className="mb-2"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Reconciliation
              </Button>
              <h1 className="text-display font-bold text-[var(--ds-text-primary)]">
                Reconciliation status
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Per-account progress across a date range. Drill through to resolve exceptions.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-[var(--ds-radius-md)] border border-[var(--ds-status-danger)]/30 bg-[var(--ds-status-danger-bg)] px-4 py-3 text-[var(--ds-status-danger)]">
            <AlertTriangle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}

        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
              <div>
                <FormLabel htmlFor="start-date">Start date</FormLabel>
                <Input
                  id="start-date"
                  type="date"
                  value={period.start_date}
                  onChange={(e) => setPeriod((p) => ({ ...p, start_date: e.target.value }))}
                  className="h-8"
                />
              </div>
              <div>
                <FormLabel htmlFor="end-date">End date</FormLabel>
                <Input
                  id="end-date"
                  type="date"
                  value={period.end_date}
                  onChange={(e) => setPeriod((p) => ({ ...p, end_date: e.target.value }))}
                  className="h-8"
                />
              </div>
              <div className="flex items-end">
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() => setActivePeriod({ ...period })}
                  disabled={!period.start_date || !period.end_date}
                >
                  Apply
                </Button>
              </div>
              <div className="flex items-end">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setPeriod(initialRange);
                    setActivePeriod(initialRange);
                  }}
                >
                  Reset
                </Button>
              </div>
              <div className="flex items-end gap-1">
                {presets.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => applyPreset(p)}
                    className="rounded border border-gray-200 px-2 py-1 text-[10px] text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-[var(--ds-accent-primary)]" />
                <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading status report…</p>
              </div>
            ) : accounts.length === 0 ? (
              <div className="p-8 text-center text-sm text-[var(--ds-text-muted)]">
                No reconcilable accounts have statement lines in this period.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead>Account</TableHead>
                    <TableHead align="right">Imported</TableHead>
                    <TableHead align="right">Matched (draft)</TableHead>
                    <TableHead align="right">Reconciled</TableHead>
                    <TableHead align="right">Unmatched</TableHead>
                    <TableHead align="right">Ignored</TableHead>
                    <TableHead align="right">Rate</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((a) => (
                    <TableRow
                      key={a.id}
                      className="cursor-pointer hover:bg-[var(--ds-surface-panel2)]"
                      onClick={() => drillThrough(a.id)}
                    >
                      <TableCell>
                        <div className="text-xs font-semibold text-[var(--ds-text-primary)]">
                          {a.account_name}
                        </div>
                        <div className="text-[10px] uppercase tracking-wide text-[var(--ds-text-muted)]">
                          {a.account_type?.replace(/_/g, ' ')} · {a.ledger_class}
                        </div>
                      </TableCell>
                      <AmountCountCell count={a.imported_count} amount={a.imported_amount} />
                      <AmountCountCell count={a.matched_count} amount={a.matched_amount} />
                      <AmountCountCell
                        count={a.reconciled_count}
                        amount={a.reconciled_amount}
                        tone="good"
                      />
                      <AmountCountCell
                        count={a.unmatched_count}
                        amount={a.unmatched_amount}
                        tone={a.unmatched_count > 0 ? 'warn' : undefined}
                      />
                      <AmountCountCell
                        count={a.ignored_count}
                        amount={a.ignored_amount}
                        tone="muted"
                      />
                      <TableCell align="right">
                        <RateCell rate={a.reconciliation_rate} />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            drillThrough(a.id);
                          }}
                        >
                          Open <ExternalLink className="ml-1 h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}

                  {totals && (
                    <TableRow className="bg-[var(--ds-surface-panel2)] font-semibold">
                      <TableCell>
                        <div className="text-xs">Totals</div>
                      </TableCell>
                      <AmountCountCell
                        count={totals.imported_count}
                        amount={totals.imported_amount}
                      />
                      <AmountCountCell
                        count={totals.matched_count}
                        amount={totals.matched_amount}
                      />
                      <AmountCountCell
                        count={totals.reconciled_count}
                        amount={totals.reconciled_amount}
                        tone="good"
                      />
                      <AmountCountCell
                        count={totals.unmatched_count}
                        amount={totals.unmatched_amount}
                        tone={totals.unmatched_count > 0 ? 'warn' : undefined}
                      />
                      <AmountCountCell
                        count={totals.ignored_count}
                        amount={totals.ignored_amount}
                        tone="muted"
                      />
                      <TableCell align="right">
                        <RateCell rate={totals.reconciliation_rate} />
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <p className="mt-3 text-[11px] text-[var(--ds-text-muted)]">
          Rate is calculated by reconciled amount ÷ imported amount so a single large uncleared
          transfer is weighted correctly.
        </p>
      </div>
    </Layout>
  );
}

function AmountCountCell({ count, amount, tone }) {
  const toneClass =
    tone === 'good'
      ? 'text-green-700 dark:text-green-400'
      : tone === 'warn'
        ? 'text-amber-700 dark:text-amber-400'
        : tone === 'muted'
          ? 'text-gray-500'
          : 'text-[var(--ds-text-primary)]';
  return (
    <TableCell align="right">
      <div className={`text-xs tabular-nums ${toneClass}`}>{currency(amount)}</div>
      <div className="text-[10px] text-[var(--ds-text-muted)]">{count} line{count === 1 ? '' : 's'}</div>
    </TableCell>
  );
}

function RateCell({ rate }) {
  if (rate == null) {
    return <span className="text-xs text-[var(--ds-text-muted)]">—</span>;
  }
  const num = Number(rate);
  const tone =
    num >= 95
      ? 'text-green-700 dark:text-green-400'
      : num >= 60
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-red-700 dark:text-red-400';
  return <span className={`text-xs font-semibold tabular-nums ${tone}`}>{num.toFixed(1)}%</span>;
}
